import type { ComponentProps, CSSProperties } from 'react';
import { Typography } from 'antd';

type AntTextProps = ComponentProps<typeof Typography.Text>;

export type TextVariant =
  | 'fine'
  | 'micro'
  | 'tiny'
  | 'caption'
  | 'body'
  | 'bodyLg'
  | 'subtitle'
  | 'titleSm'
  | 'title'
  | 'pageTitle'
  | 'pageTitleLg'
  | 'hero'
  | 'heroLg'
  | 'eyebrow'
  | 'eyebrowSm';

export type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold';

export type TextTone =
  | 'default'
  | 'secondary'
  | 'tertiary'
  | 'disabled'
  | 'error'
  | 'success'
  | 'warning'
  | 'fair'
  | 'primary'
  | 'heading'
  | 'onCharcoal';

export type TextFamily = 'body' | 'display' | 'headline' | 'mono' | 'figures';

export interface TextProps extends AntTextProps {
  variant?: TextVariant;
  weight?: TextWeight;
  tone?: TextTone;
  family?: TextFamily;
  maxWidth?: CSSProperties['maxWidth'];
  /**
   * Emit heading semantics without changing a pixel. `pageTitle` carries the page-title
   * *scale* but renders `Typography.Text` — a `<span>` — so screens built from it had no
   * `h1`..`h6` at all and no landmark for "what page is this". Opt-in rather than baked into
   * the variant because three callers use `pageTitle` for a modal title, which is not the
   * page's heading.
   */
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
}

const VARIANT_STYLES: Record<TextVariant, CSSProperties> = {
  // 10px running text, sentence case (NPJB-852): the design's compact table cells — rank,
  // play, rationale, chip labels — at their 11–11.5px sizes rounded down to the grid.
  fine: { fontSize: 'var(--dz-font-size-2xs)' },
  // 12px, not 10: 10px is for caps labels only (T4/T6, NPJB-818 AC5). The names stay so the
  // ~30 call sites need no edit; `eyebrow` (caps) and `fine` are the 10px variants.
  micro: { fontSize: 'var(--dz-font-size-xs)' },
  tiny: { fontSize: 'var(--dz-font-size-xs)' },
  caption: { fontSize: 'var(--dz-font-size-xs)' },
  body: { fontSize: 'var(--dz-font-size-sm)' },
  bodyLg: { fontSize: 'var(--dz-font-size-md)' },
  subtitle: { fontSize: 'var(--dz-font-size-lg)' },
  titleSm: { fontSize: 'var(--dz-font-size-xl)' },
  title: { fontSize: 'var(--dz-font-size-2xl)' },
  pageTitle: { fontSize: 'var(--dz-font-size-3xl)', fontWeight: 600 },
  pageTitleLg: { fontSize: 'var(--dz-font-size-4xl)', fontWeight: 600 },
  hero: { fontSize: 'var(--dz-font-size-5xl)', fontWeight: 700 },
  heroLg: { fontSize: 'var(--dz-font-size-7xl)', fontWeight: 700 },
  eyebrow: {
    fontSize: 'var(--dz-font-size-2xs)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  eyebrowSm: {
    fontSize: 'var(--dz-font-size-2xs)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
};

const WEIGHT_STYLES: Record<TextWeight, CSSProperties> = {
  regular: { fontWeight: 400 },
  medium: { fontWeight: 500 },
  semibold: { fontWeight: 600 },
  bold: { fontWeight: 700 },
};

const TONE_STYLES: Record<TextTone, CSSProperties> = {
  default: { color: 'var(--dz-body)' },
  secondary: { color: 'var(--dz-blue-muted)' },
  tertiary: { color: 'var(--dz-muted)' },
  disabled: { color: 'var(--dz-disabled-text)' },
  // Semantic tones take each family's readable `-shade` step, not its mid-strength hue:
  // the mid hues are built for icons, chart series and chip fills, and every one of them
  // lands under 4.5:1 as text (warning 2.82:1 on white, 2.43:1 on its own tint). Spec 31 · D6
  // ruled this for `Tag`; the same reasoning binds `Text`.
  error: { color: 'var(--dz-error)' },
  success: { color: 'var(--dz-tonal-sage-shade)' },
  warning: { color: 'var(--dz-tone-peach-fg)' },
  fair: { color: 'var(--dz-tone-peach-fg)' },
  // C4 — Bright Blue is for links ≥24px and stat callouts. Every `tone='primary'` call site
  // is caption or eyebrow sized, where 4.33:1 fails; small text takes the AA-safe blue.
  primary: { color: 'var(--dz-link-blue)' },
  heading: { color: 'var(--dz-charcoal)' },
  onCharcoal: { color: 'var(--dz-on-charcoal)' },
};

const FAMILY_STYLES: Record<TextFamily, CSSProperties> = {
  body: { fontFamily: 'var(--dz-font-body)' },
  display: { fontFamily: 'var(--dz-font-display)' },
  headline: { fontFamily: 'var(--dz-font-heading)' },
  mono: { fontFamily: 'var(--dz-font-mono)' },
  // Bold tabular figures — counts, ranks, KPI values (NPJB-852). `mono` stays for codes (T8).
  figures: { fontFamily: 'var(--dz-font-figures)' },
};

// Variants that force Bodoni Moda display italic regardless of `family` prop.
const DISPLAY_ITALIC_VARIANTS: ReadonlySet<TextVariant> = new Set([
  'pageTitle',
  'pageTitleLg',
  'hero',
  'heroLg',
]);

// Variants that force Roboto (body) family regardless of `family` prop —
// per spec, we never render the display serif in all-caps.
const FORCE_INTER_VARIANTS: ReadonlySet<TextVariant> = new Set(['eyebrow', 'eyebrowSm']);

function resolveFamilyStyle(
  forceBody: boolean,
  displayItalic: boolean,
  family: TextFamily | undefined,
): CSSProperties | null {
  if (forceBody) return FAMILY_STYLES.body;
  if (displayItalic) return { ...FAMILY_STYLES.display, fontStyle: 'italic' };
  return family ? FAMILY_STYLES[family] : null;
}

export const Text = ({
  variant,
  weight,
  tone,
  family,
  maxWidth,
  headingLevel,
  style,
  ...rest
}: TextProps) => {
  const isDisplayItalicVariant = !!variant && DISPLAY_ITALIC_VARIANTS.has(variant);
  const isForceInterVariant = !!variant && FORCE_INTER_VARIANTS.has(variant);

  // Determine resolved font family + italic.
  // - eyebrow*: always Roboto, no italic (caps).
  // - pageTitle / pageTitleLg / hero / heroLg: always Bodoni Moda italic.
  // - family="display": Bodoni Moda italic.
  // - family="headline": Jost (display sans / stat callouts), upright.
  const useDisplayItalic = isDisplayItalicVariant || (!isForceInterVariant && family === 'display');

  const familyStyle = resolveFamilyStyle(isForceInterVariant, useDisplayItalic, family);

  const merged: CSSProperties = {
    ...(variant ? VARIANT_STYLES[variant] : null),
    ...(weight ? WEIGHT_STYLES[weight] : null),
    ...(tone ? TONE_STYLES[tone] : null),
    ...(familyStyle ?? null),
    ...(maxWidth ? { maxWidth } : null),
    ...style,
  };

  const headingProps = headingLevel
    ? ({ role: 'heading', 'aria-level': headingLevel } as const)
    : null;

  return <Typography.Text {...rest} {...headingProps} style={merged} />;
};
